// packages/web-ui/src/components/inline-cards/ChangesetPreviewCard.tsx
//
// Visual diff card that renders ABOVE the `<KbChangeTreeShell>` for the
// same changesetId. Subscribes to the changeset-previews store (populated
// by the `kb:changesetPreview` SSE dispatcher) for the pre-laid-out
// `ReactFlowLayoutJson`. Each node/edge carries a `data.changeKind` tag
// that `<PartNode>` / `<ConnectionEdge>` translate into a color variant
// (green = added, amber = updated, red dashed = removed).
//
// The card auto-fits the React Flow canvas and is non-interactive (same
// pattern as `<MiniReactFlowPreview>`). No buttons — the user accepts /
// rejects entities via the `<KbChangeTreeShell>` below the diff.

import { useStore } from 'zustand';
import type { JSX } from 'react';
import type { StoreApi } from 'zustand';
import { CardShell } from './CardShell.js';
import { MiniReactFlowPreview } from './MiniReactFlowPreview.js';
import type { ChangesetPreviewEnvelopeData } from '../../state/inline-cards.js';
import {
  getChangesetPreviewsStore,
  selectChangesetPreviewLayout,
  type ChangesetPreviewsState,
} from '../../state/changeset-previews.js';

export interface ChangesetPreviewCardProps
  extends ChangesetPreviewEnvelopeData {
  /**
   * Optional store override — production wiring passes the singleton from
   * `getChangesetPreviewsStore()`; tests inject their own
   * `createChangesetPreviewsStore()` for isolation.
   */
  store?: StoreApi<ChangesetPreviewsState>;
}

export function ChangesetPreviewCard(
  props: ChangesetPreviewCardProps,
): JSX.Element {
  const store = props.store ?? getChangesetPreviewsStore();
  const layout = useStore(store, selectChangesetPreviewLayout(props.changesetId));

  if (layout === undefined) {
    return (
      <CardShell
        role="region"
        ariaLabel="Changeset preview loading"
        cardKind="changeset-preview"
        testId="changeset-preview-card"
      >
        <div
          data-testid="changeset-preview-loading"
          className="bg-cream-200 animate-pulse rounded-none border border-taupe-300"
          style={{ width: 560, height: 220 }}
          aria-label="Loading visual diff"
        />
      </CardShell>
    );
  }

  return (
    <CardShell
      role="region"
      ariaLabel={`Changeset preview: ${layout.title ?? 'Proposed changes'}`}
      cardKind="changeset-preview"
      testId="changeset-preview-card"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <div
            className="font-medium text-fg"
            data-testid="changeset-preview-title"
          >
            {layout.title ?? 'Proposed changes'}
          </div>
          <span
            data-testid="changeset-preview-badge"
            aria-label="Visual diff overlay"
            className="text-[10px] font-medium uppercase tracking-wide text-emerald-700"
          >
            DIFF VIEW
          </span>
        </div>
        <ChangesetPreviewLegend />
        <div
          className="reactflow-preview overflow-hidden"
          data-testid="changeset-preview-canvas"
        >
          <MiniReactFlowPreview layout={layout} width={620} height={360} />
        </div>
      </div>
    </CardShell>
  );
}

/**
 * Inline legend explaining the color codes + numbered-badge convention.
 * Keeps the demo recording self-explanatory without forcing the user to
 * mouse over each block.
 */
function ChangesetPreviewLegend(): JSX.Element {
  return (
    <div
      data-testid="changeset-preview-legend"
      className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wide text-taupe-700"
    >
      <LegendSwatch label="Added" color="#10B981" />
      <LegendSwatch label="Updated" color="#F59E0B" />
      <LegendSwatch label="Removed" color="#F43F5E" dashed />
      <span
        data-testid="changeset-preview-legend-numbered"
        className="text-taupe-700 normal-case"
        aria-label="Numbered badges match the [N] prefix on each row of the change list below"
      >
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            minWidth: 16,
            height: 16,
            lineHeight: '16px',
            padding: '0 4px',
            borderRadius: 8,
            background: '#10B981',
            color: '#FFFFFF',
            fontSize: 9,
            fontWeight: 700,
            marginRight: 4,
            verticalAlign: 'middle',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          }}
        >
          N
        </span>
        Numbered badges = list row [N] below
      </span>
    </div>
  );
}

function LegendSwatch(props: {
  label: string;
  color: string;
  dashed?: boolean;
}): JSX.Element {
  return (
    <span
      data-testid={`changeset-preview-legend-${props.label.toLowerCase()}`}
      className="inline-flex items-center gap-1"
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 14,
          height: 6,
          borderRadius: 0,
          border: `1.5px ${props.dashed ? 'dashed' : 'solid'} ${props.color}`,
          background: props.dashed ? 'transparent' : props.color,
        }}
      />
      <span>{props.label}</span>
    </span>
  );
}
